/**
 * Zajednička logika ekrana sa listom dokumenata magacina (Dopuna, Izdavanje, Povrat, Otpis): pretvara podatke starog
 * taba (warehouseDocumentTabMixin) u kolone i redove za WarehouseDocScreen. Stari tab ostaje izvor učitavanja, filtriranja,
 * štampe i Excela; ovde se samo gradi prikaz.
 *
 * Ekran daje (computed `docConfig`):
 *   icon, hintKey (naziv "poslednji unos"), emptyTitleKey, emptyTextKey, dateKey (naziv kolone datuma),
 *   open (naziv metode starog taba koja otvara dokument), remove (naziv metode za brisanje ili null),
 *   action: { key, label, icon, tone }, run (naziv metode za novi dokument)
 * i po potrebi `docExtraColumns` (kolone između ID-a i "Kreirano").
 */
const ALIGN = { right: "right", center: "center" };

export default {
  data: () => ({
    docPageSize: 100,
  }),
  computed: {
    docGrouped() {
      return (this.filters.warehouse || []).length > 1;
    },
    /** Zbirni prikaz po artiklu i otvaranje reda sa dokumentima (ekran može da ih isključi). */
    docAggregateAvailable() {
      return true;
    },
    docExpandable() {
      return true;
    },
    docExtraColumns() {
      return [
        { key: "number", label: this.$t("warehouse.document_number"), main: true, mobile: "title" },
        { key: "document_date", label: this.docConfig.dateLabel, mobile: "meta" },
        { key: "notes", label: this.$t("note"), mobile: "meta" },
      ];
    },
    docColumns() {
      return [
        { key: "id", label: "ID", mobile: "sub", muted: true, num: true, prefix: "#" },
        ...this.docExtraColumns,
        { key: "created_at", label: this.$t("status_created"), mobile: "meta" },
      ];
    },
    docRows() {
      const items = this.customSearch(this.displayedDocuments, this.headers, this.filters.search);
      return items.map((item) => this.docRow(item));
    },
    aggregateColumnsView() {
      return this.aggregateHeaders
        .filter((header) => header.value !== "data-table-expand")
        .map((header) => ({
          key: header.value,
          label: header.text,
          align: ALIGN[header.align],
          main: header.value === "grocery_name",
          mobile: header.value === "grocery_name" ? "title" : header.value === "grocery_code" ? "sub" : "meta",
        }));
    },
    aggregateRowsView() {
      return this.activeAggregatedItems.map((item, index) => ({
        key: item.grocery_code || `${item.grocery_name}-${index}`,
        raw: item,
        grocery_code: item.grocery_code || "",
        grocery_name: this.toLatin(item.grocery_name) || "",
        measure: item.measure || "",
        total_quantity: this.formatNumber(item.total_quantity),
        avg_unit_price: this.formatNumber(item.avg_unit_price),
        total_amount: this.formatNumber(item.total_amount),
        document_count: item.document_count,
        sort: {
          total_quantity: Number(item.total_quantity) || 0,
          avg_unit_price: Number(item.avg_unit_price) || 0,
          total_amount: Number(item.total_amount) || 0,
        },
      }));
    },
    detailColumnsView() {
      return this.expandedSubHeaders
        .filter((header) => header.value !== "actions")
        .map((header, index) => ({
          key: header.value,
          label: header.text,
          align: ALIGN[header.align],
          mobile: index === 0 ? "title" : "meta",
        }));
    },
    docHint() {
      return this.lastEntryDate ? `${this.$t(this.docConfig.hintKey)}: ${this.lastEntryDate}` : "";
    },
    docEmpty() {
      if (this.aggregateMode) {
        return {
          icon: "mdi-sigma",
          title: this.$t("warehouse.no_aggregate_data"),
          text: this.$t("warehouse.no_entries_for_period"),
        };
      }
      const config = this.docConfig;
      return {
        icon: config.icon,
        title: this.documents.length ? this.$t("warehouse.no_entries_for_period") : this.$t(config.emptyTitleKey),
        text: this.lastEntryDate ? `${this.$t(config.hintKey)}: ${this.lastEntryDate}` : this.$t(config.emptyTextKey),
      };
    },
    docActions() {
      return [this.docConfig.action];
    },
    docProps() {
      return {
        loading: this.loading,
        columns: this.docColumns,
        rows: this.docRows,
        grouped: this.docGrouped,
        aggregateAvailable: this.docAggregateAvailable,
        aggregate: this.aggregateMode,
        aggregateColumns: this.aggregateColumnsView,
        aggregateRows: this.aggregateRowsView,
        expandable: this.docExpandable,
        detailColumns: this.detailColumnsView,
        detailRows: this.detailRowsFor,
        hint: this.docHint,
        hintClickable: Boolean(this.lastEntryDate),
        actions: this.docActions,
        empty: this.docEmpty,
        clickable: true,
        pageSize: this.docPageSize,
      };
    },
    docKpiExtra() {
      return [];
    },
    shownKpis() {
      if (this.loading) {
        return [];
      }
      return [
        {
          key: "documents",
          label: this.$t("warehouse_shell.documents"),
          value: this.aggregateMode ? this.aggregateRowsView.length : this.docRows.length,
          tone: "inverse",
        },
        ...this.docKpiExtra,
      ];
    },
  },
  watch: {
    shownKpis: {
      handler(kpis) {
        this.$emit("kpis", kpis);
      },
      immediate: true,
    },
    loading: {
      handler(loading) {
        this.$emit("loading", loading);
      },
      immediate: true,
    },
  },
  methods: {
    /** Red tabele za dokument; `raw` je stavka starog taba (za radnje). */
    docRow(item) {
      return {
        key: item.id,
        raw: item,
        group: { id: item.warehouse_id, name: this.toLatin(item.warehouse?.name || "") || this.$t("warehouse.unknown") },
        id: item.id,
        number: item.number || "",
        supplier: (item.supplier && item.supplier.name) || "",
        document_date: item.document_date ? this.localeDate(item.document_date) : "",
        notes: item.notes || "",
        total_items: item.groceries ? item.groceries.length : 0,
        created_at: item.created_at ? this.formatDocDateTime(item.created_at) : "",
        sort: {
          id: Number(item.id) || 0,
          document_date: item.document_date || "",
          created_at: item.created_at || "",
          total_items: item.groceries ? item.groceries.length : 0,
        },
      };
    },
    formatDocDateTime(value) {
      return this.localeDate(value, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    /** Dokumenti jednog artikla u zbirnom prikazu (otvoreni red). */
    detailRowsFor(row) {
      return this.getDocumentsForGroceryWithDetails(row.grocery_code).map((doc) => ({
        key: doc.id,
        raw: doc,
        number: doc.number || "",
        document_date: doc.document_date ? this.localeDate(doc.document_date) : "",
        "supplier.name": (doc.supplier && doc.supplier.name) || "",
        supplier_invoice_number: doc.supplier_invoice_number || "",
        "warehouse.name": this.toLatin((doc.warehouse && doc.warehouse.name) || ""),
        grocery_quantity: `${this.formatNumber(doc.grocery_quantity)} ${doc.grocery_measure || ""}`.trim(),
        grocery_value: this.formatNumber(doc.grocery_value),
      }));
    },
    cellText(value) {
      return value === undefined || value === null || value === "" ? "—" : value;
    },
    openRowDoc(row) {
      this[this.docConfig.open](row.raw);
    },
    deleteRowDoc(row) {
      if (this.docConfig.remove) {
        this[this.docConfig.remove](row.raw);
      }
    },
    onDocAction() {
      this[this.docConfig.run]();
    },
  },
};
